// JAPAN WEB CLINIC (Dawn) — the product page on Dawn's main-product:
// - "カートに追加する" (the page's button, the bottom bar's button, Enter) never adds to the cart here: Dawn's form
//   submit is stopped and the receiving step opens with the chosen variant (plan §3③). The cart gets the medicine
//   once, when the receiving step is confirmed (step ④, assets/jwc-receive.js).
// - Dawn's variant change re-renders our parts too: every [data-jwc-update] (per month, the per-month table, the bottom
//   bar) is copied from the section HTML Dawn fetched. While Dawn loads a variant, or the combination is sold out /
//   unavailable, the bottom bar's button is disabled like Dawn's own.
// - The bottom bar moves under <body> so Dawn's scroll animations (transform) never pin it to a column.
(() => {
  'use strict';
  const J = window.JWC;
  const info = document.querySelector('product-info[id^="MainProduct-"]');
  if (!J || !info) return;
  const sectionId = info.dataset.section;
  const form = document.getElementById(`product-form-${sectionId}`);
  const submit = document.getElementById(`ProductSubmitButton-${sectionId}`);
  if (!form || !submit) return;
  const PICK_KEY = 'jwc-pick';

  const bar = document.querySelector('[data-jwc-product-bar]');
  if (bar && bar.parentElement !== document.body) document.body.appendChild(bar);

  // ---------------------------------------------------------------- the chosen variant → the receiving step
  const blocked = () => submit.disabled || submit.getAttribute('aria-disabled') === 'true' || submit.classList.contains('loading');
  function chosen() {
    const id = Number(form.querySelector('input[name="id"]')?.value);
    return Number.isFinite(id) && id > 0 ? id : null;
  }
  function savePick(variantId) {
    const pick = { productId: Number(info.dataset.productId), handle: info.dataset.url, variantId, at: Date.now() };
    try {
      sessionStorage.setItem(PICK_KEY, JSON.stringify(pick));
    } catch (e) {
      /* kept in memory below */
    }
    J.pick = pick;
    return pick;
  }
  // the receiving step's 「3か月分にする」 (over the carry guide) changes the pick (assets/jwc-receive.js)
  J.setPick = (variantId) => savePick(variantId);
  J.readPick = () => {
    if (J.pick) return J.pick;
    try {
      return JSON.parse(sessionStorage.getItem(PICK_KEY) || 'null');
    } catch (e) {
      return null;
    }
  };
  // Back on the product after the pick changed in the receiving step: Dawn's picker shows it too (each option's input,
  // then one change event, so Dawn loads that variant as if chosen by hand)
  function syncPicker() {
    const p = J.readPick();
    if (!p || Number(p.productId) !== Number(info.dataset.productId) || Number(p.variantId) === chosen()) return;
    let data = null;
    try {
      data = JSON.parse(document.querySelector('[data-jwc-receive-data]')?.textContent || 'null');
    } catch (e) {
      data = null;
    }
    const options = data?.product?.variants?.[p.variantId]?.options;
    const picker = info.querySelector('variant-selects');
    if (!options || !picker) return;
    const groups = picker.querySelectorAll(':scope > fieldset, :scope > .product-form__input--dropdown');
    let last = null;
    options.forEach((value, i) => {
      const group = groups[i];
      if (!group) return;
      const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find((r) => r.value === value);
      if (radio) {
        if (!radio.checked) last = radio;
        radio.checked = true;
        return;
      }
      const select = group.querySelector('select');
      const option = select && Array.from(select.options).find((o) => o.value === value);
      if (!option || select.value === value) return;
      Array.from(select.options).forEach((o) => o.toggleAttribute('selected', o === option));
      select.value = value;
      last = select;
    });
    last?.dispatchEvent(new Event('change', { bubbles: true }));
  }
  // The receiving step (step ④) is on the same URL with ?step=receive. assets/jwc-receive.js listens for jwc:step.
  J.showStep = (step, push = true) => {
    const url = new URL(location.href);
    if (step) url.searchParams.set('step', step);
    else url.searchParams.delete('step');
    if (push) history.pushState({ jwcStep: step || null }, '', url);
    document.documentElement.classList.toggle('jwc-step-mode', Boolean(step));
    if (bar) bar.toggleAttribute('data-off', Boolean(step));
    J.updateBars?.();
    document.dispatchEvent(new CustomEvent('jwc:step', { detail: { step: step || null } }));
    window.scrollTo({ top: 0 });
    if (!step) syncPicker();
  };
  window.addEventListener('popstate', () => J.showStep(new URL(location.href).searchParams.get('step'), false));

  // capture phase on the document: runs before Dawn's product-form listener on the form, which never sees the event
  document.addEventListener(
    'submit',
    (event) => {
      if (event.target !== form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const id = chosen();
      if (blocked() || !id) return;
      savePick(id);
      J.showStep('receive');
    },
    true,
  );
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-jwc-atc]')) return;
    event.preventDefault();
    if (!blocked()) form.requestSubmit();
  });

  // ---------------------------------------------------------------- variant change
  const parts = () => Array.from(document.querySelectorAll('[data-jwc-update]'));
  function mirrorButton() {
    const off = blocked();
    const label = submit.querySelector('span')?.textContent.trim();
    document.querySelectorAll('[data-jwc-atc]').forEach((button) => {
      button.disabled = off;
      if (label) button.textContent = label;
    });
    // Dawn hides the price of an unavailable combination: hide ours with it
    const unavailable = off && label === window.variantStrings?.unavailable;
    parts().forEach((el) => el.classList.toggle('hidden', unavailable));
  }
  new MutationObserver(mirrorButton).observe(submit, { attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'], childList: true, subtree: true });

  // (Dawn's PUB_SUB_EVENTS is a top-level const of constants.js: global, but not a property of window)
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (!data || data.sectionId !== sectionId || !data.html) return;
      parts().forEach((el) => {
        const fresh = data.html.querySelector(`[data-jwc-update="${el.dataset.jwcUpdate}"]`);
        if (fresh) el.innerHTML = fresh.innerHTML;
      });
      mirrorButton();
      J.updateBars?.();
    });
  }

  function start() {
    mirrorButton();
    const step = new URL(location.href).searchParams.get('step');
    if (step === 'receive') J.showStep('receive', false);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
